import FreeDNSWorkspace from "@/components/FreeDNSWorkspace";
import DomainOperations from "@/components/DomainOperations";
import { useEffect, useState, useCallback } from "react";
import {
  Network, Save, RefreshCw, Rocket, ShieldCheck, ShieldAlert, Loader2, Copy,
  ExternalLink, KeyRound, CheckCircle2, XCircle, AlertTriangle, Link2, Plus, Globe, Server,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const Section = ({ title, icon: Icon, children, actions }) => (
  <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">
        {Icon && <Icon className="h-3.5 w-3.5" />} {title}
      </div>
      {actions}
    </div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const dot = (ok) => ok === true ? "bg-emerald-400" : ok === false ? "bg-rose-400" : "bg-amber-400";

export default function FreeDNS() {
  const { canWrite } = useAuth();
  const [st, setSt] = useState(null);
  const [sites, setSites] = useState([]);
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({ domain: "", vpsIpv4: "", directUpdateUrl: "" });
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [updRes, setUpdRes] = useState(null);
  const [httpsRows, setHttpsRows] = useState(null);
  const [newLink, setNewLink] = useState({ subdomain: "", destination_slug: "" });

  const load = useCallback(async () => {
    const s = await api.get("/freedns/status").catch(() => ({}));
    setSt(s);
    setForm((f) => ({ domain: s.domain || "", vpsIpv4: s.vpsIpv4 || "40.160.100.231", directUpdateUrl: "" }));
    api.get("/sites").then(setSites).catch(() => {});
    api.get("/links").then((l) => setLinks(l.filter((x) => x.provider === "freedns"))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => { setBusy(key); try { return await fn(); } finally { setBusy(""); } };

  const saveConn = () => run("save", async () => {
    try {
      const body = { domain: form.domain, vpsIpv4: form.vpsIpv4, wildcardDestinationSiteId: st.wildcardDestinationSiteId };
      if (replacing && form.directUpdateUrl) body.directUpdateUrl = form.directUpdateUrl;
      await api.post("/freedns/config", body);
      toast.success("Connection saved"); setReplacing(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  });

  const saveDest = (slug) => run("dest", async () => {
    try { await api.post("/freedns/config", { domain: form.domain, vpsIpv4: form.vpsIpv4, wildcardDestinationSiteId: slug === "__none__" ? null : slug }); toast.success("Wildcard destination set"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  });

  const testWildcard = () => run("test", async () => {
    try { const r = await api.post("/freedns/test"); setTestRes(r); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  });

  const updateDns = () => run("update", async () => {
    try { const r = await api.post("/freedns/update"); setUpdRes(r); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); setUpdRes({ ok: false, message: e?.response?.data?.detail || "Update failed" }); }
  });

  const createLink = () => run("link", async () => {
    try {
      await api.post("/links", { name: newLink.subdomain, subdomain: newLink.subdomain, destination_slug: newLink.destination_slug, provider: "freedns" });
      toast.success("Named route saved; DNS and website verification are still required"); setNewLink({ subdomain: "", destination_slug: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  });

  const checkHttps = () => run("https", async () => {
    try { const r = await api.post("/freedns/https", {}); setHttpsRows(r.checks); }
    catch (e) { toast.error("HTTPS check failed"); }
  });

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (!st) return <Loading label="Loading FreeDNS" />;
  const domain = st.domain || form.domain;

  return (
    <div data-testid="freedns-page" className="space-y-4">
      <FreeDNSWorkspace />

      <details className="border border-zinc-800 rounded-lg p-4"><summary className="text-xs text-zinc-500 cursor-pointer">Legacy DDNS configuration and named routes</summary><div className="space-y-4 mt-4">
      <p className="text-xs text-amber-200">Saving a named route does not create or verify DNS records. Use Create Links above to create and verify Wave links.</p>

      {/* Status card */}
      <Section title="FreeDNS" icon={Network} actions={
        <span className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-mono",
          st.configured ? "border-emerald-500/20 text-emerald-400 bg-emerald-500/5" : "border-zinc-700 text-zinc-400")}>
          <span className={cn("h-1.5 w-1.5 rounded-full", dot(st.configured))} /> {st.configured ? "Connected" : "Not connected"}
        </span>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs">
          <div><div className="text-zinc-600 uppercase">Domain</div><div className="text-zinc-200 mt-1">{domain || "—"}</div></div>
          <div><div className="text-zinc-600 uppercase">Wildcard</div><div className="text-zinc-200 mt-1">*.{domain}</div></div>
          <div><div className="text-zinc-600 uppercase">VPS</div><div className="text-zinc-200 mt-1">{st.vpsIpv4 || "—"}</div></div>
          <div><div className="text-zinc-600 uppercase">Dynamic DNS</div><div className="mt-1 flex items-center gap-1">{st.directUpdateConfigured ? <span className="text-emerald-400">✓ Configured</span> : <span className="text-zinc-500">— not set</span>}</div></div>
          <div><div className="text-zinc-600 uppercase">Wildcard DNS</div><div className="mt-1">{st.wildcardVerified ? <span className="text-emerald-400">✓ Verified</span> : <span className="text-amber-400">unverified</span>}</div></div>
          <div><div className="text-zinc-600 uppercase">Last DNS Test</div><div className="text-zinc-300 mt-1">{st.lastTestAt ? new Date(st.lastTestAt).toLocaleString() : "—"}</div></div>
          <div><div className="text-zinc-600 uppercase">Last DDNS Update</div><div className="text-zinc-300 mt-1">{st.lastUpdateAt ? new Date(st.lastUpdateAt).toLocaleString() : "—"}</div></div>
          <div><div className="text-zinc-600 uppercase">Wildcard Dest</div><div className="text-zinc-300 mt-1">{st.wildcardDestinationSiteId || "—"}</div></div>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Connection */}
        <Section title="FreeDNS Connection" icon={Network}>
          <Field label="FreeDNS Domain"><Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} disabled={!canWrite} className="bg-zinc-900 border-zinc-800 font-mono" data-testid="fd-domain" /></Field>
          <Field label="VPS IPv4"><Input value={form.vpsIpv4} onChange={(e) => setForm({ ...form, vpsIpv4: e.target.value })} disabled={!canWrite} className="bg-zinc-900 border-zinc-800 font-mono" data-testid="fd-ip" /></Field>
          <Field label="Direct Update URL">
            {st.directUpdateConfigured && !replacing ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-zinc-400">••••••••••••••••••••</span>
                <span className="text-emerald-400 text-xs font-mono flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Configured</span>
                {canWrite && <button onClick={() => setReplacing(true)} data-testid="fd-replace-secret" className="ml-auto text-xs rounded border border-zinc-800 px-2 py-1 hover:bg-zinc-800 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Replace Secret</button>}
              </div>
            ) : (
              <Input type="password" placeholder="https://sync.afraid.org/u/SECRET/" value={form.directUpdateUrl} onChange={(e) => setForm({ ...form, directUpdateUrl: e.target.value })} disabled={!canWrite} className="bg-zinc-900 border-zinc-800 font-mono" data-testid="fd-secret" />
            )}
          </Field>
          {canWrite && <button onClick={saveConn} disabled={busy === "save"} data-testid="fd-save" className="mt-1 inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white disabled:opacity-50">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Connection</button>}
        </Section>

        {/* Dynamic DNS */}
        <Section title="Dynamic DNS" icon={Rocket}>
          <p className="text-sm text-zinc-400 mb-3">Push your VPS IP to FreeDNS. The secret request runs on the backend only.</p>
          {canWrite && <button onClick={updateDns} disabled={busy === "update" || !st.directUpdateConfigured} data-testid="fd-update" className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white disabled:opacity-50">{busy === "update" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Update DNS Now</button>}
          {!st.directUpdateConfigured && <p className="text-xs text-amber-400 mt-2 font-mono">Add the Direct Update URL first.</p>}
          {updRes && (
            <div className={cn("mt-3 rounded-md border px-3 py-2 text-sm font-mono", updRes.ok ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-300" : "border-rose-500/20 bg-rose-500/5 text-rose-300")} data-testid="fd-update-result">
              {updRes.status === "up_to_date" ? <>✓ DNS is already up to date<div className="text-zinc-400">Current IP: {updRes.ip}</div></>
                : updRes.ok ? <>✓ DNS updated successfully<div className="text-zinc-400">{updRes.ip}</div></>
                : <>✕ {updRes.message}</>}
            </div>
          )}
        </Section>

        {/* Wildcard DNS test */}
        <Section title="Wildcard DNS" icon={ShieldCheck} actions={canWrite && <button onClick={testWildcard} disabled={busy === "test"} data-testid="fd-test" className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-800">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Test Wildcard DNS</button>}>
          {!testRes ? <p className="text-sm text-zinc-500 font-mono">Run a live lookup of a random <span className="text-zinc-300">cannon-xxxx.{domain}</span> host.</p> : (
            <div className="font-mono text-xs space-y-2" data-testid="fd-test-result">
              <div><span className="text-zinc-600">Test host:</span> <span className="text-zinc-200">{testRes.host}</span></div>
              <div><span className="text-zinc-600">Resolved:</span> {testRes.resolved.length ? testRes.resolved.map((ip) => <span key={ip} className="text-zinc-200 ml-1">{ip}</span>) : <span className="text-rose-400 ml-1">no A record</span>}</div>
              <div><span className="text-zinc-600">VPS:</span> <span className="text-zinc-200">{testRes.vps}</span></div>
              <div><span className="text-zinc-600">VPS Match:</span> {testRes.match ? <span className="text-emerald-400">✓ YES</span> : <span className="text-rose-400">✕ NO</span>}</div>
              {testRes.multiple && <div className="flex items-start gap-1.5 text-amber-400"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> More than one A record is resolving. Remove old records from FreeDNS. Also detected: {testRes.extra_addresses.join(", ")}</div>}
              {testRes.ok && <div className="text-emerald-400">✓ Wildcard DNS is configured correctly.</div>}
            </div>
          )}
        </Section>

        {/* Wildcard destination */}
        <Section title="Wildcard Destination" icon={Globe}>
          <p className="text-sm text-zinc-400 mb-3">Any hostname with no specific route opens this site.</p>
          <Select value={st.wildcardDestinationSiteId || "__none__"} onValueChange={saveDest} disabled={!canWrite}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="fd-dest"><SelectValue placeholder="Choose hosted website" /></SelectTrigger>
            <SelectContent className="bg-[#0c0c0e] border-zinc-800">
              <SelectItem value="__none__">None</SelectItem>
              {sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name} ({s.type})</SelectItem>)}
            </SelectContent>
          </Select>
        </Section>
      </div>

      {/* FreeDNS Links */}
      <Section title="FreeDNS Links" icon={Link2}>
        {canWrite && (
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div><label className="text-xs text-zinc-500 uppercase tracking-widest">Subdomain</label>
              <div className="flex items-center mt-1"><Input value={newLink.subdomain} onChange={(e) => setNewLink({ ...newLink, subdomain: e.target.value })} placeholder="games" className="bg-zinc-900 border-zinc-800 font-mono rounded-r-none w-40" data-testid="fd-link-sub" /><span className="font-mono text-xs text-zinc-500 border border-l-0 border-zinc-800 rounded-r-md px-2 py-2 bg-zinc-900">.{domain}</span></div></div>
            <div><label className="text-xs text-zinc-500 uppercase tracking-widest">Destination</label>
              <Select value={newLink.destination_slug} onValueChange={(v) => setNewLink({ ...newLink, destination_slug: v })}>
                <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-800 w-52" data-testid="fd-link-dest"><SelectValue placeholder="Choose site" /></SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-zinc-800">{sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <button onClick={createLink} disabled={busy === "link" || !newLink.subdomain || !newLink.destination_slug} data-testid="fd-link-create" className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-2 text-sm font-medium hover:bg-white disabled:opacity-50"><Plus className="h-4 w-4" /> Save named route</button>
          </div>
        )}
        {links.length === 0 ? <p className="text-sm text-zinc-500 font-mono">No FreeDNS links yet.</p> : (
          <div className="space-y-1">
            {links.map((l) => {
              const host = `${l.subdomain}.${l.base_domain || domain}`;
              return (
                <div key={l.link_id} className="flex items-center gap-2 rounded-md border border-zinc-800 px-3 py-2" data-testid={`fd-link-${l.subdomain}`}>
                  <Link2 className="h-3.5 w-3.5 text-sky-400" />
                  <span className="font-mono text-xs text-sky-400">{host}</span>
                  <span className="text-zinc-600">→</span>
                  <span className="font-mono text-xs text-violet-400">{l.destination_slug}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => copy(`https://${host}`)} className="p-1 text-zinc-500 hover:text-zinc-200" title="Copy URL"><Copy className="h-3.5 w-3.5" /></button>
                    <a href={`https://${host}`} target="_blank" rel="noopener noreferrer" className="p-1 text-zinc-500 hover:text-zinc-200" title="Open Website"><ExternalLink className="h-3.5 w-3.5" /></a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* HTTPS status */}
      <Section title="HTTPS Status" icon={ShieldAlert} actions={<button onClick={checkHttps} disabled={busy === "https"} data-testid="fd-https-check" className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-800">{busy === "https" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh Status</button>}>
        <p className="text-xs text-zinc-500 font-mono mb-3">DNS and HTTPS are checked separately — DNS resolving does not mean HTTPS is ready.</p>
        {!httpsRows ? <p className="text-sm text-zinc-500">Run a check to verify certificates for your FreeDNS hostnames.</p> : httpsRows.length === 0 ? <p className="text-sm text-zinc-500 font-mono">No FreeDNS hostnames to check yet.</p> : (
          <div className="border border-zinc-800 rounded-md overflow-hidden">
            <table className="w-full text-xs font-mono">
              <thead><tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-zinc-500 uppercase"><th className="px-3 py-2">Hostname</th><th className="px-3 py-2">DNS</th><th className="px-3 py-2">HTTPS</th><th className="px-3 py-2">Certificate</th><th className="px-3 py-2">Expires</th></tr></thead>
              <tbody>
                {httpsRows.map((r) => (
                  <tr key={r.hostname} className="border-b border-zinc-800/70" data-testid={`fd-https-${r.hostname}`}>
                    <td className="px-3 py-2 text-zinc-200">{r.hostname}</td>
                    <td className="px-3 py-2">{r.dns ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}</td>
                    <td className="px-3 py-2">{r.https ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}</td>
                    <td className={cn("px-3 py-2", r.https ? "text-emerald-400" : "text-rose-400")}>{r.certificate}</td>
                    <td className="px-3 py-2 text-zinc-400">{r.expires ? new Date(r.expires).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* How it works */}
      <Section title="How it works" icon={Network}>
        <pre className="text-xs font-mono text-zinc-400 leading-relaxed">
{`FreeDNS Wildcard   *.${domain}
        ↓
VPS   ${st.vpsIpv4 || "40.160.100.231"}
        ↓
Hosting Admin Router
        ↓
Named Link / Wildcard Destination
        ↓
Static Site or Node App`}
        </pre>
      </Section>
      </div></details>
    </div>
  );
}
